import React, { forwardRef, useMemo } from 'react';
import { Box, useTheme, Stack, Typography, Alert } from '@mui/material';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { useLocalization } from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { IconLibrary, PermissionButton } from '@product-framework/index';
import { observer } from 'mobx-react';
import ContactRow from './ContactRow';

const EMPTY_FIELD = {
  phoneNumber: '',
  description: '',
  isVoiceEnabled: false,
  isVoiceDefault: false,
  isTextEnabled: false,
  isTextDefault: false,
};

function createHeaders(formatMessage, theme) {
  const light = 50;
  const dark = 100;
  return {
    action: { key: 'action', width: { md: 0.5 }, textAlign: 'center' },
    phoneNumber: {
      key: 'phoneNumber',
      label: formatMessage({
        id: 'INSIGHT_CONTACT_PHONE_NUMBER_HEADER',
        defaultMessage: 'Phone Number',
      }),
      width: { md: 3 },
    },
    description: {
      key: 'description',
      label: formatMessage({
        id: 'INSIGHT_CONTACT_DESCRIPTION_HEADER',
        defaultMessage: 'Description',
      }),
      width: { md: 2.5 },
    },
    isVoiceEnabled: {
      key: 'isVoiceEnabled',
      label: 'Voice',
      width: { md: 1.5 },
      backgroundColor: theme.palette.grey[light],
    },
    isVoiceDefault: {
      key: 'isVoiceDefault',
      label: 'Default for Voice',
      width: { md: 1.5 },
      backgroundColor: theme.palette.grey[light],
    },
    isTextEnabled: {
      key: 'isTextEnabled',
      label: 'SMS Opt-In',
      width: { md: 1.5 },
      backgroundColor: theme.palette.grey[dark],
    },
    isTextDefault: {
      key: 'isTextDefault',
      label: 'Default for SMS',
      width: { md: 1.5 },
      backgroundColor: theme.palette.grey[dark],
    },
  };
}

const InsightContact = forwardRef((props, ref) => {
  const { formatMessage } = useLocalization();
  const theme = useTheme();

  const controlName = props.name;
  const maxFields = props.maxFields ?? 20;

  const { control, setValue } = useFormContext();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: controlName,
    keyName: 'id',
  });

  const headers = useMemo(
    () => createHeaders(formatMessage, theme),
    [formatMessage, theme],
  );
  const headersArray = Object.values(headers);

  // formPermissions is comoing from fieldgenerator
  const permission = props?.formPermissions?.[props?.name]?.phoneNumber;

  if (permission === 'READ' && (!fields || fields.length === 0)) {
    return <Typography variant="body2">No contacts added</Typography>;
  }

  return (
    <Box
      ref={ref}
      sx={{
        mt: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 1,
      }}>
      {Array.isArray(fields) && fields.length > 0 && (
        <Alert severity="info" sx={{ marginBottom: 1 }}>
          By selecting SMS Opt-In, you agree to receive text message
          notifications about your jobs and updates from Service Xcelerator.
          <ul style={{ margin: 0, padding: '10px 0 0 17px' }}>
            <li>
              Your phone number will not be shared with the customer or sold or
              shared with third parties for marketing or promotional purposes.
            </li>
            <li>
              Msg & Data rates may apply. Msg frequency varies based on service
              activity. Reply HELP for help.
            </li>
            <li>
              You can opt out anytime by changing this setting or replying STOP
              to any SMS message.
            </li>
          </ul>
          <Stack
            sx={{ justifySelf: 'start', mt: 2 }}
            direction="row"
            spacing={2}>
            <a target="_blank" href="/legal/terms">
              Terms
            </a>
            <a target="_blank" href="/legal/privacy">
              Privacy
            </a>
          </Stack>
        </Alert>
      )}

      <Grid2 container>
        {headersArray.map((h, index) => {
          return (
            <Grid2
              sx={{
                alignContent: 'center',
                alignItems: 'center',
                p: 1,
                backgroundColor: h.backgroundColor,
              }}
              key={h.key}
              xs
              md={h.width.md}
              textAlign={index < 3 ? 'left' : 'center'}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent={index < 3 ? 'flex-start' : 'center'}
                gap={0.5}>
                <Typography variant="subtitle2" color="tertiary">
                  {h.label}
                </Typography>
              </Box>
            </Grid2>
          );
        })}
      </Grid2>
      <Stack spacing={0}>
        {fields.map((fieldItem, idx) => (
          <ContactRow
            key={fieldItem.id}
            idx={idx}
            control={control}
            controlName={controlName}
            remove={remove}
            update={update}
            fields={fields}
            permissions={props?.formPermissions}
            setValue={setValue}
            headers={headers}
          />
        ))}
      </Stack>

      {fields.length < maxFields && (
        <PermissionButton
          variant="text"
          startIcon={<IconLibrary.Add />}
          onClick={() => append({ ...EMPTY_FIELD })}
          permissionName="CAN_ADD_SP_INSIGHT_CONTACTS">
          {formatMessage({
            id: 'InsightContact_ADD_BUTTON',
            defaultMessage: 'Add Contact',
          })}
        </PermissionButton>
      )}
    </Box>
  );
});

export default observer(InsightContact);
